import type { SortOrder } from '@/data-viz/data-source.interface';
import type { BuildingId } from '@/interfaces/Building.interface';
import type { UnzonedDateTime } from '@/interfaces/Date.interface';
import type { SensorTimeseriesId } from '@/interfaces/Timeseries.interface';
import { APIError } from '@/providers/errors.constants';
import { InformationProvider } from '@/providers/information/information.provider';
import { AdvizeoModalService } from '@/services/advizeo-modal.service';
import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { PaginatedResource } from '@/shared/utils/angular-sdk';
import { ButtonStyle } from '@/ui/button/ui-button';
import { PaginatedResourceSignals } from '@/utils/angular/signals/paginated-resource-signals';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, type Observable, Subject } from 'rxjs';
import { type AlertScenarioId, AlertScenarioType, type AlertStatPeriodCode, type AlertValidation } from '../../alerts.interface';
import { AdvizeoCoreAlertScenariosService } from '../../services/advizeo-core-alert-scenarios.service';
import type {
    WaterLeakageAlertScenariosFilters,
    WaterLeakageCustomAlertEditorModel,
    WaterLeakageScenario,
    WaterLeakageScenarioAlertsTimeseriesAnalysis,
    WaterLeakageScenarioCreation,
    WaterLeakageScenarioGlobalSummary,
    WaterLeakageScenarioSensorStat,
    WaterLeakageScenarioStat,
    WaterLeakageScenarioSummary,
    WaterLeakageScenarioUpdate
} from '../water-leakage-alert-scenario.interface';
import { AdvizeoCoreWaterLeakageScenarioService } from './advizeo-core-water-leakage-scenario.service';

@Injectable({
    providedIn: 'root'
})
export class WaterLeakageScenarioService {
    private _advizeoCoreWaterLeakageScenarioProvider = inject(AdvizeoCoreWaterLeakageScenarioService);
    private _informationProvider = inject(InformationProvider);
    private _translateService = inject(TranslateService);
    private _modalService = inject(AdvizeoModalService);
    private _advizeoCoreAlertScenariosProvider = inject(AdvizeoCoreAlertScenariosService);

    private _alertsScenariosListUpdate$ = new Subject<void>();

    getWaterLeakageScenario$(scenarioId: AlertScenarioId): Observable<WaterLeakageScenario> {
        return this._advizeoCoreWaterLeakageScenarioProvider.getWaterLeakageScenario$(scenarioId);
    }

    createWaterLeakageScenario(waterLeakageScenarioValue: WaterLeakageScenarioCreation): Promise<AlertScenarioId> {
        return this._advizeoCoreWaterLeakageScenarioProvider.createWaterLeakageScenario(waterLeakageScenarioValue);
    }

    updateWaterLeakageScenario(alertScenarioId: AlertScenarioId, scenarioUpdate: WaterLeakageScenarioUpdate): Promise<void> {
        return this._advizeoCoreWaterLeakageScenarioProvider.updateWaterLeakageScenario(alertScenarioId, scenarioUpdate);
    }

    saveAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._advizeoCoreWaterLeakageScenarioProvider.saveAlertScenario(alertScenarioId);
    }

    publishAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._advizeoCoreWaterLeakageScenarioProvider.publishAlertScenario(alertScenarioId);
    }

    getScenarioValidation$(alertScenarioId: AlertScenarioId): Observable<AlertValidation> {
        return this._advizeoCoreWaterLeakageScenarioProvider.getAlertScenarioValidation$(alertScenarioId);
    }

    extractErrorsFromApiFailure(errorCode: APIError, payload: APIError[]): APIError[] {
        if (errorCode === APIError.INVALID_CONFIGURATION) {
            return payload;
        } else {
            return [errorCode];
        }
    }

    showErrors(errors: APIError[]) {
        if (errors.length === 1) {
            switch (errors[0]) {
                case APIError.ALREADY_USED_NAME:
                    this._informationProvider.showToast(
                        this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.ALREADY_USED_NAME'),
                        'error'
                    );
                    break;
                case APIError.INVALID_DATE_RANGE:
                    this._informationProvider.showToast(
                        this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.INVALID_DATE_RANGE'),
                        'error'
                    );
                    break;

                default:
                    this._informationProvider.showToast(
                        this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.UNKNOWN_SINGLE_ERROR'),
                        'error'
                    );
                    break;
            }
        } else {
            this._informationProvider.showToast(this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.MULTIPLE_ERRORS'), 'error');
        }
    }

    getAlertsScenarios(
        params: () => { periodCode: AlertStatPeriodCode; isProjectAdmin: boolean; filters: WaterLeakageAlertScenariosFilters }
    ): PaginatedResourceSignals<WaterLeakageScenarioSummary> {
        return new PaginatedResourceSignals(
            params,
            ({ periodCode, isProjectAdmin, filters }, page, pageSize) =>
                firstValueFrom(
                    this._advizeoCoreWaterLeakageScenarioProvider.fetchAlertScenarios$(
                        { page, size: pageSize },
                        periodCode,
                        isProjectAdmin,
                        filters
                    )
                ),
            {
                dataChanged$: this._alertsScenariosListUpdate$
            }
        );
    }

    private deleteAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._advizeoCoreAlertScenariosProvider
            .deleteAlertScenario(alertScenarioId)
            .then(() => this._alertsScenariosListUpdate$.next());
    }

    requestAlertScenarioDeletion(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._modalService
            .openDialog({
                title: this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.TITLE'),
                message: this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.DESCRIPTION'),
                buttons: [
                    {
                        text: this._translateService.instant('BUTTONS.VALIDATE'),
                        onClick: () => {
                            this.deleteAlertScenario(alertScenarioId)
                                .then(() => {
                                    this._informationProvider.showToast(
                                        this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.SUCCESS'),
                                        'success'
                                    );
                                })
                                .catch(() => {
                                    this._informationProvider.showToast(
                                        this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.ERROR'),
                                        'error'
                                    );
                                });
                        },
                        color: SharedUiColorNameV2.PRIMARY
                    },
                    {
                        text: this._translateService.instant('BUTTONS.CANCEL'),
                        onClick: () => {},
                        color: SharedUiColorNameV2.PRIMARY,
                        buttonStyle: ButtonStyle.OUTLINE
                    }
                ]
            })
            .afterClosed()
            .then();
    }

    setAlertScenarioEnabled(alertScenarioId: AlertScenarioId, enabled: boolean): Promise<void> {
        return this._advizeoCoreAlertScenariosProvider
            .setAlertScenarioEnabled(alertScenarioId, AlertScenarioType.WATER_LEAKAGE, enabled)
            .then(() => this._alertsScenariosListUpdate$.next());
    }

    getWaterLeakageScenarioAlertsTimeseriesAnalysis$(
        scenarioId: AlertScenarioId,
        timeseriesId: SensorTimeseriesId,
        startDate: UnzonedDateTime,
        endDate: UnzonedDateTime
    ): Observable<WaterLeakageScenarioAlertsTimeseriesAnalysis> {
        return this._advizeoCoreWaterLeakageScenarioProvider.getWaterLeakageScenarioAlertsTimeseriesAnalysis$(
            scenarioId,
            timeseriesId,
            startDate,
            endDate
        );
    }

    getAlertScenarioBuildingsStats(alertScenarioId: AlertScenarioId): PaginatedResource<
        WaterLeakageScenarioStat,
        {
            periodCode: AlertStatPeriodCode;
            search: string;
            sort?:
                | {
                      name: string;
                      direction: SortOrder;
                  }[]
                | null;
        }
    > {
        return new PaginatedResource(({ periodCode, search, sort }, page, pageSize) =>
            firstValueFrom(
                this._advizeoCoreWaterLeakageScenarioProvider.fetchAlertScenarioBuildingsStats$(
                    { page, size: pageSize, sort },
                    alertScenarioId,
                    periodCode,
                    search
                )
            )
        );
    }

    getAlertScenarioSensorsStats$(
        alertScenarioId: AlertScenarioId,
        buildingId: BuildingId,
        startDate: UnzonedDateTime,
        endDate: UnzonedDateTime
    ): Observable<WaterLeakageScenarioSensorStat[]> {
        return this._advizeoCoreWaterLeakageScenarioProvider.fetchAlertScenarioSensorStats$(
            alertScenarioId,
            buildingId,
            startDate,
            endDate
        );
    }

    getAlertScenarioSummary$(alertScenarioId: AlertScenarioId, periodCode: AlertStatPeriodCode): Observable<WaterLeakageScenarioSummary> {
        return this._advizeoCoreWaterLeakageScenarioProvider.getAlertScenarioSummary$(alertScenarioId, periodCode);
    }

    getAlertScenarioGlobalSummary$(periodCode: AlertStatPeriodCode): Observable<WaterLeakageScenarioGlobalSummary> {
        return this._advizeoCoreWaterLeakageScenarioProvider.getAlertScenarioGlobalSummary$(periodCode);
    }

    declareAlert(
        alertScenarioId: AlertScenarioId,
        timeseriesId: SensorTimeseriesId,
        customAlert: WaterLeakageCustomAlertEditorModel
    ): Promise<void> {
        return this._advizeoCoreWaterLeakageScenarioProvider.declareAlert(alertScenarioId, timeseriesId, customAlert);
    }

    deleteAnnotatedAlert(alertScenarioId: AlertScenarioId, alertId: number): Promise<void> {
        return this._advizeoCoreWaterLeakageScenarioProvider.deleteAnnotatedAlert(alertScenarioId, alertId);
    }

    updateAnnotatedAlert(
        alertScenarioId: AlertScenarioId,
        alertId: number,
        customAlert: WaterLeakageCustomAlertEditorModel
    ): Promise<void> {
        return this._advizeoCoreWaterLeakageScenarioProvider.updateAnnotatedAlert(alertScenarioId, alertId, customAlert);
    }
}
